import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The single source of truth for "is the app reachable right now" (spec §11).
 *
 * `navigator.onLine === false` is trustworthy in the negative direction only —
 * `true` merely means a network interface exists, not that the backend answers.
 * That is why this hook pairs the browser events with an explicit `reconnect()`
 * probe: the offline surfaces (OfflineNotice, ViewState status="offline") need
 * a button that actually *checks*, not one that only re-reads a flag.
 *
 * @param {object} [options]
 * @param {string} [options.probeUrl='/api/health/live'] - cheap endpoint hit by `reconnect()`.
 * @param {number} [options.probeTimeout=4000] - ms before a probe counts as offline.
 * @param {(info: {viaProbe: boolean}) => void} [options.onReconnect] - fired on every
 *   offline → online transition, including the one a successful probe causes.
 *   Use it to re-run the request that failed while the connection was gone.
 * @returns {{
 *   online: boolean,
 *   offline: boolean,
 *   checking: boolean,
 *   wasOffline: boolean,
 *   offlineSince: number|null,
 *   reconnect: () => Promise<boolean>
 * }}
 */
export function useOnlineStatus(options = {}) {
  const { probeUrl = '/api/health/live', probeTimeout = 4000, onReconnect } = options;

  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false
  );
  const [checking, setChecking] = useState(false);
  const [offlineSince, setOfflineSince] = useState(() =>
    typeof navigator !== 'undefined' && navigator.onLine === false ? Date.now() : null
  );
  const [wasOffline, setWasOffline] = useState(false);

  // Kept in a ref so the effect below never has to re-subscribe when the
  // caller passes an inline arrow function (which is the common case).
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;

  const goOnline = useCallback((viaProbe) => {
    setOnline((prev) => {
      if (!prev) {
        setWasOffline(true);
        onReconnectRef.current?.({ viaProbe });
      }
      return true;
    });
    setOfflineSince(null);
  }, []);

  const goOffline = useCallback(() => {
    setOnline((prev) => prev === false ? prev : false);
    setOfflineSince((prev) => prev ?? Date.now());
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleOnline = () => goOnline(false);
    const handleOffline = () => goOffline();
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [goOnline, goOffline]);

  /**
   * Actively verify the connection. Resolves to the new online state so a
   * caller can `if (await reconnect()) retry()`.
   */
  const reconnect = useCallback(async () => {
    if (typeof fetch !== 'function') {
      goOnline(true);
      return true;
    }

    setChecking(true);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), probeTimeout) : null;

    try {
      const response = await fetch(probeUrl, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        signal: controller?.signal
      });
      // Any HTTP answer — even a 503 — proves the network path works; only a
      // 5xx from a *reachable* server would be a backend problem, and that is
      // an error state, not an offline state.
      if (response) {
        goOnline(true);
        return true;
      }
      goOffline();
      return false;
    } catch {
      goOffline();
      return false;
    } finally {
      if (timer) clearTimeout(timer);
      setChecking(false);
    }
  }, [probeUrl, probeTimeout, goOnline, goOffline]);

  return { online, offline: !online, checking, wasOffline, offlineSince, reconnect };
}

export default useOnlineStatus;
